import * as Phaser from "phaser"
import { EventBus } from "../core/event-bus"
import { AUDIO, EVENT } from "../core/keys"
import {
  BUMPER_FEEDBACK,
  type BumperFeedbackType,
  getCalloutValues,
} from "../data/bumper-feedback"
import type { BumperEffectType, GameData, GameSprite } from "../types"
import type { VfxManager } from "./vfx-manager"

const HEAL_AMOUNT = 12
const LEVELUP_XP_AMOUNT = 6
const BANK_DROP_COUNT = 4
const BANK_DROP_VALUE = 8
const MAX_BUMPER_BONUS_LEVEL = 5
const HEAL_BY_LEVEL = [8, 12, 18, 26, 36] as const
const LEVELUP_XP_BY_LEVEL = [4, 6, 9, 13, 18] as const
const BANK_DROP_COUNT_BY_LEVEL = [3, 4, 5, 6, 8] as const
const BANK_DROP_VALUE_BY_LEVEL = [5, 7, 9, 12, 16] as const
const GUARD_INVULNERABILITY_BY_LEVEL_MS = [650, 850, 1050, 1250, 1450] as const
const GUARD_INVULNERABILITY_MS = 950

export type BumperBonusLevel = 1 | 2 | 3 | 4 | 5

interface BumperEffectsOptions {
  scene: Phaser.Scene
  vfx: VfxManager
  enemyGroup: Phaser.Physics.Arcade.Group
  onDefeatEnemy: (enemy: GameSprite) => void
  onDamageBossEnemy: (enemy: GameSprite, ratio: number, color: number) => void
  spawnCoins: (x: number, y: number, value: number) => void
  healPlayer: (amount: number) => void
  grantXp: (amount: number) => void
}

export class BumperEffects {
  private readonly options: BumperEffectsOptions

  public constructor(options: BumperEffectsOptions) {
    this.options = options
  }

  public apply(
    bumperSprite: GameSprite,
    effect: BumperEffectType,
    dogSprite: GameSprite,
    gameData: GameData,
    level: BumperBonusLevel = 1,
  ): void {
    this.showBumperCallout(bumperSprite, effect, level)

    switch (effect) {
      case "guard":
        this.applyGuardEffect(dogSprite, gameData, level)
        return
      case "bank":
        this.applyBankEffect(bumperSprite, level)
        return
      case "heal":
        this.applyHealEffect(dogSprite, level)
        return
      case "levelup":
        this.applyLevelUpEffect(bumperSprite, level)
        return
      case "charge":
        return
    }
  }

  private applyGuardEffect(
    dogSprite: GameSprite,
    gameData: GameData,
    level: BumperBonusLevel,
  ): void {
    const { scene, vfx } = this.options
    const durationMs = this.getLevelValue(
      GUARD_INVULNERABILITY_BY_LEVEL_MS,
      level,
      GUARD_INVULNERABILITY_MS,
    )

    gameData.player.isInvulnerable = true
    gameData.player.invulnerabilityTimer = Math.max(
      gameData.player.invulnerabilityTimer,
      durationMs,
    )
    dogSprite.setTint(0xa78bfa)
    scene.time.delayedCall(durationMs, () => {
      if (!gameData.player.isInvulnerable && dogSprite.active) {
        dogSprite.clearTint()
      }
    })
    vfx.flashScreen(0x8b5cf6, 0.16, 90)
  }

  private applyBankEffect(
    bumperSprite: GameSprite,
    level: BumperBonusLevel,
  ): void {
    const { vfx } = this.options
    vfx.flashScreen(0xf59e0b, 0.12, 100)

    const dropCount = this.getLevelValue(
      BANK_DROP_COUNT_BY_LEVEL,
      level,
      BANK_DROP_COUNT,
    )
    const dropValue = this.getLevelValue(
      BANK_DROP_VALUE_BY_LEVEL,
      level,
      BANK_DROP_VALUE,
    )
    for (let i = 0; i < dropCount; i++) {
      const angle = Phaser.Math.DegToRad(
        -34 + i * (68 / Math.max(1, dropCount - 1)),
      )
      const distance = 28 + i * 7
      this.options.spawnCoins(
        bumperSprite.x + Math.cos(angle) * distance + 18,
        bumperSprite.y + Math.sin(angle) * distance,
        dropValue,
      )
    }
    vfx.pickupEmitter.explode(10 + level * 4, bumperSprite.x, bumperSprite.y)
  }

  private applyHealEffect(
    dogSprite: GameSprite,
    level: BumperBonusLevel,
  ): void {
    const { vfx, healPlayer, scene } = this.options
    const healAmount = this.getLevelValue(HEAL_BY_LEVEL, level, HEAL_AMOUNT)

    healPlayer(healAmount)
    vfx.flashScreen(0x22d3ee, 0.12, 100)

    // Play heal sound
    if (scene.sound.get(AUDIO.HEAL)) {
      scene.sound.play(AUDIO.HEAL, { volume: 0.5 })
    }

    // Visual feedback on hero
    vfx.emitImpactBurst(dogSprite.x, dogSprite.y, 0x22d3ee, 1.0)
  }

  private applyLevelUpEffect(
    bumperSprite: GameSprite,
    level: BumperBonusLevel,
  ): void {
    const { vfx, grantXp } = this.options
    const xpAmount = this.getLevelValue(
      LEVELUP_XP_BY_LEVEL,
      level,
      LEVELUP_XP_AMOUNT,
    )

    grantXp(xpAmount)
    vfx.flashScreen(0x22c55e, 0.12, 100)

    // Visual feedback
    vfx.showDamageNumber(
      bumperSprite.x,
      bumperSprite.y - 20,
      xpAmount,
      0x22c55e,
      false, // Is XP, not damage
    )
    vfx.pickupEmitter.explode(8 + level * 3, bumperSprite.x, bumperSprite.y)
  }

  private showBumperCallout(
    _bumperSprite: GameSprite,
    type: BumperFeedbackType,
    level: BumperBonusLevel = 1,
  ): void {
    if (type === "charge") return

    const def = BUMPER_FEEDBACK[type]
    const values = getCalloutValues(type, level)
    const text = this.getCalloutText(type, values)
    if (!text) return

    EventBus.emit(EVENT.BUMPER_TIP, {
      text,
      color: def.color,
    })
  }

  private getCalloutText(
    type: BumperFeedbackType,
    values: ReturnType<typeof getCalloutValues>,
  ): string {
    switch (type) {
      case "bank":
        return `+${values.value1 * (values.value2 ?? 1)} COINS`
      case "guard":
        return `INVINCIBLE ${values.value1}S`
      case "heal":
        return `+${values.value1} HP`
      case "levelup":
        return `+${values.value1} XP`
      case "charge":
        return ""
    }
  }

  private getLevelValue<T extends number>(
    values: readonly T[],
    level: BumperBonusLevel,
    fallback: T,
  ): T {
    return values[Math.min(MAX_BUMPER_BONUS_LEVEL, level) - 1] ?? fallback
  }
}
